import { useNavigate, Link } from "react-router-dom";
import { useEffect } from "react";
import { useI18n } from '../../i18n';
import { useBoundStore } from '../../store';
import { login } from '../../services/Authentication';
import { getOwnUser } from '../../services/User';
import FormComponent from '../forms';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import queriLogo from '../../assets/logo.png';

const LoginPage = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const setAccessToken = useBoundStore((state) => state.setAccessToken);
  const setRefreshToken = useBoundStore((state) => state.setRefreshToken);
  const setUser = useBoundStore((state) => state.setUser);
  const isAuthenticated = useBoundStore((state) => state.isAuthenticated);

  // Redirect to dashboard if already authenticated
  useEffect(() => {
    if (isAuthenticated()) {
      navigate("/", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleLoginSubmit = async (formData: Record<string, any>) => {
    setAccessToken("");
    setRefreshToken("");
    setUser(null);

    const response = await login(formData.email, formData.password);

    if (!response.success || !response.data) {
      return { success: false, errors: response.errors };
    }

    const accessToken = response.data.accessToken;
    setAccessToken(accessToken);
    setRefreshToken(response.data.refreshToken);

    // Pass token directly to avoid race condition with interceptor
    const userResponse = await getOwnUser(accessToken);
    if (!userResponse.success || !userResponse.data) {
      return { success: false, errors: userResponse.errors };
    }

    setUser(userResponse.data);

    navigate("/");
    return { success: true }
  };

  return (
    <div className="flex justify-center items-center h-full">
      <div className="w-full max-w-md space-y-6">
        <div className="flex flex-col items-center gap-1">
          <img
            src={queriLogo}
            alt="Queri"
            className="h-10 max-w-[240px] object-contain mb-3"
          />
          <h1 className="text-2xl font-bold">
            {t.auth.login.title}
          </h1>
          <p className="text-sm text-muted-foreground">{t.auth.login.subtitle}</p>
        </div>
        <FormComponent onSubmit={handleLoginSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">{t.auth.login.emailLabel}</Label>
            <Input
              id="email"
              name="email"
              type="email"
              required
            />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="password">{t.auth.login.passwordLabel}</Label>
              <Link to="/forgot-password" className="text-sm text-muted-foreground hover:underline">
                {t.auth.login.forgotPasswordLink}
              </Link>
            </div>
            <Input
              id="password"
              name="password"
              type="password"
              required
            />
          </div>
          <Button type="submit" className="w-full mt-2">
            {t.auth.login.submit}
          </Button>
        </FormComponent>
      </div>
    </div>
  )
}

export default LoginPage
